// Opening with no signal. Words: IEA_OFFLINE_SHELL_WORDS.md rev 3, section 3 (approved 2026-10-14).
// Every page opens offline. Only the IEA Site Survey works offline. Every other page says
// Needs Signal in plain words, then loads by itself when signal comes back.
// Pure helpers plus two small browser calls. Unit tested: tests/unit/offline.test.ts.

import { esc } from "./training-screens";
import { watchForUpdates } from "./app-update";

/** Admin addresses that keep working with no signal. The survey lives under #/survey. */
export function opensOffline(hash: string): boolean {
  const h = hash.replace(/^#\/?/, "");
  // The IEA Site Survey and the Asset ID Creator both run from the site pack with no signal.
  return h === "survey" || h.startsWith("survey/") || h === "assets" || h.startsWith("assets/");
}

/** True when the device says it has a network. A false here is reliable; a true is a hint. */
export function isOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

/** The page shown in place of a page that needs the cloud. */
export function needsSignalScreen(what = "This page", offerSurvey = false): string {
  const line = what === "Admin pages" ? "Admin pages read from the cloud." : `${what} reads from the cloud.`;
  return `<div class="inner"><div class="box"><div class="boxhd"><div class="icon grey">!</div>
    <div class="nm"><b>Needs Signal</b><small>${esc(line)}</small></div></div>
    <div style="padding:18px 24px;display:flex;flex-direction:column;gap:10px">
    <span>No signal right now. It opens by itself when signal comes back.</span>
    <span class="muted">The IEA Site Survey works with no signal.</span>
    ${offerSurvey ? '<button class="sv-btn gold" type="button" data-offline-survey="1">Open IEA Site Survey</button>' : ""}</div></div></div>`;
}

/** Shows Needs Signal and reloads once the device is back online. */
export function holdForSignal(root: HTMLElement, what?: string, offerSurvey = false): void {
  root.innerHTML = needsSignalScreen(what, offerSurvey);
  root.querySelector("[data-offline-survey]")?.addEventListener("click", () => {
    location.hash = "#/survey";
    location.reload();
  });
  window.addEventListener("online", () => location.reload(), { once: true });
}

/**
 * Registers the service worker (public/sw.js) on the built app only. The dev server never
 * registers one, so a local preview always runs fresh code.
 */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => watchForUpdates(reg))
      .catch(() => undefined);
  });
}
